import { useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useDropZone } from "../lib/useDropZone";
import { compressImage } from "../lib/compressImage";
import { Paperclip, FileText, X, Loader2, Upload } from "lucide-react";

export function DocumentUploader({ docs, onChange, testid = "document-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFiles = async (files) => {
    setError("");
    const arr = Array.from(files);
    setUploading(true);
    let current = docs;
    try {
      for (const file of arr) {
        // Iter123k241 · se è un'immagine, viene compressa client-side; i PDF/doc passano invariati
        const prepared = await compressImage(file);
        const fd = new FormData();
        fd.append("file", prepared);
        const { data } = await api.post("/uploads/documento", fd, { headers: { "Content-Type": "multipart/form-data" } });
        current = [...current, { id: data.id, filename: data.filename || "Documento" }];
        onChange(current);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const { dragProps, isDragOver } = useDropZone(handleFiles, { disabled: uploading });

  const remove = (id) => onChange(docs.filter((d) => d.id !== id));

  return (
    <div data-testid={testid} {...dragProps}
      className={`relative rounded-lg transition-all w-full min-w-0 ${isDragOver ? "ring-2 ring-brand bg-brand/5 p-2" : ""}`}>
      {isDragOver && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-brand/10 z-10">
          <div className="flex items-center gap-2 text-brand font-bold text-sm bg-white/95 px-3 py-1.5 rounded-full border-2 border-brand shadow-md">
            <Upload className="w-4 h-4" /> Rilascia per allegare
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2 w-full min-w-0">
        {docs.map((d) => (
          <div key={d.id} data-testid={`doc-item-${d.id}`}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-slate-200 bg-white w-full min-w-0">
            <FileText className="w-5 h-5 text-brand shrink-0" />
            <span className="truncate text-base text-slate-800 min-w-0 flex-1">{d.filename}</span>
            <button type="button" data-testid={`remove-doc-${d.id}`} onClick={() => remove(d.id)}
              className="text-red-600 hover:text-red-700 shrink-0" aria-label="Rimuovi">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button type="button" data-testid="add-document-button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="h-12 px-4 w-full sm:w-fit sm:max-w-full rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center sm:justify-start gap-2 text-slate-600 font-semibold hover:border-[#C98AA0] hover:text-brand">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
          {uploading ? "Carico..." : "Allega file / documento"}
        </button>
      </div>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*" multiple className="hidden"
        onChange={(e) => e.target.files && handleFiles(e.target.files)} />
      <p className="text-sm text-slate-500 mt-1">Formati ammessi: PDF, JPG, PNG (max 20 MB ciascuno). Puoi anche trascinare i file qui.</p>
      {error && <div className="mt-2 text-base text-red-700 font-semibold">{error}</div>}
    </div>
  );
}
